import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Download, Phone, Mail } from "lucide-react";
import { ContactFilters } from "@/components/admin/ContactFilters";
import { ContactLeadActionsMenu } from "@/components/admin/ContactLeadActionsMenu";
import { CopyButton } from "@/components/admin/CopyButton";

interface ContactSubmission {
  id: string;
  name: string;
  phone: string;
  email: string;
  service: string;
  message: string | null;
  status: string;
  lead_status: string;
  priority: string;
  lead_quality: string | null;
  call_type: string | null;
  estimated_value: number | null;
  follow_up_date: string | null;
  created_at: string;
}

const AdminLeads = () => {
  const { user, loading } = useAuth();
  const { toast } = useToast();
  const [submissions, setSubmissions] = useState<ContactSubmission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [leadStatusFilter, setLeadStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [leadQualityFilter, setLeadQualityFilter] = useState("all");
  const [callTypeFilter, setCallTypeFilter] = useState("all");
  const [stats, setStats] = useState({
    total: 0,
    new: 0,
    contacted: 0,
    converted: 0,
  });

  useEffect(() => {
    if (user) {
      loadSubmissions();
    }
  }, [user]);

  const loadSubmissions = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from("contact_submissions")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      toast({
        title: "Error",
        description: "Failed to load contact submissions",
        variant: "destructive",
      });
    } else if (data) {
      setSubmissions(data);
      setStats({
        total: data.length,
        new: data.filter((s) => s.lead_status === "new").length,
        contacted: data.filter((s) => s.lead_status === "contacted").length,
        converted: data.filter((s) => s.lead_status === "converted").length,
      });
    }
    setIsLoading(false);
  };

  const updateStatus = async (id: string, status: string) => {
    const { error } = await supabase
      .from("contact_submissions")
      .update({ lead_status: status, last_contact_date: new Date().toISOString() })
      .eq("id", id);

    if (error) {
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Success",
        description: "Status updated successfully",
      });
      loadSubmissions();
    }
  };

  const updatePriority = async (id: string, priority: string) => {
    const { error } = await supabase
      .from("contact_submissions")
      .update({ priority })
      .eq("id", id);

    if (error) {
      toast({
        title: "Error",
        description: "Failed to update priority",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Success",
        description: "Priority updated successfully",
      });
      loadSubmissions();
    }
  };

  const exportToCSV = () => {
    const headers = ["Name", "Phone", "Email", "Service", "Status", "Priority", "Quality", "Created At"];
    const csvData = filteredSubmissions.map((sub) => [
      sub.name,
      sub.phone,
      sub.email,
      sub.service,
      sub.lead_status,
      sub.priority,
      sub.lead_quality || "N/A",
      new Date(sub.created_at).toLocaleString(),
    ]);

    const csv = [headers, ...csvData].map((row) => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `contact-leads-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      new: "default",
      contacted: "secondary",
      qualified: "outline",
      converted: "default",
      unqualified: "destructive",
      callback_scheduled: "secondary",
      lost: "destructive",
    };
    return variants[status] || "default";
  };

  const getPriorityBadge = (priority: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      urgent: "destructive",
      high: "default",
      medium: "secondary",
      low: "outline",
    };
    return variants[priority] || "secondary";
  };

  const filteredSubmissions = submissions.filter((submission) => {
    const matchesSearch =
      submission.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      submission.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      submission.phone.includes(searchTerm);
    const matchesLeadStatus = leadStatusFilter === "all" || submission.lead_status === leadStatusFilter;
    const matchesPriority = priorityFilter === "all" || submission.priority === priorityFilter;
    const matchesQuality = leadQualityFilter === "all" || submission.lead_quality === leadQualityFilter;
    const matchesType = callTypeFilter === "all" || submission.call_type === callTypeFilter;

    return matchesSearch && matchesLeadStatus && matchesPriority && matchesQuality && matchesType;
  });

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen">Loading submissions...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold mb-2">Contact Form Leads</h1>
          <p className="text-muted-foreground">Manage leads from contact form submissions</p>
        </div>
        <Button onClick={exportToCSV}>
          <Download className="mr-2 h-4 w-4" />
          Export CSV
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Total Leads</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">New</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.new}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Contacted</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.contacted}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Converted</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.converted}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <ContactFilters
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            leadStatus={leadStatusFilter}
            onLeadStatusChange={setLeadStatusFilter}
            priority={priorityFilter}
            onPriorityChange={setPriorityFilter}
            leadQuality={leadQualityFilter}
            onLeadQualityChange={setLeadQualityFilter}
            callType={callTypeFilter}
            onCallTypeChange={setCallTypeFilter}
          />
        </CardContent>
      </Card>

      <div className="space-y-4">
        {filteredSubmissions.length === 0 ? (
          <Card>
            <CardContent className="py-8">
              <p className="text-center text-muted-foreground">No contact submissions found</p>
            </CardContent>
          </Card>
        ) : (
          filteredSubmissions.map((submission) => (
            <Card key={submission.id}>
              <CardContent className="pt-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-lg font-semibold">{submission.name}</h3>
                      <Badge variant={getStatusBadge(submission.lead_status)}>
                        {submission.lead_status.replace("_", " ")}
                      </Badge>
                      <Badge variant={getPriorityBadge(submission.priority)}>{submission.priority}</Badge>
                      {submission.lead_quality && (
                        <Badge variant="outline" className="capitalize">
                          {submission.lead_quality}
                        </Badge>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <a href={`tel:${submission.phone}`} className="hover:underline">
                          {submission.phone}
                        </a>
                        <CopyButton text={submission.phone} label="Phone" />
                      </div>
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <a href={`mailto:${submission.email}`} className="hover:underline">
                          {submission.email}
                        </a>
                        <CopyButton text={submission.email} label="Email" />
                      </div>
                      <div>
                        <span className="font-medium">Service:</span> {submission.service}
                      </div>
                      <div>
                        <span className="font-medium">Submitted:</span>{" "}
                        {new Date(submission.created_at).toLocaleString()}
                      </div>
                      {submission.estimated_value && (
                        <div>
                          <span className="font-medium">Est. Value:</span> ${submission.estimated_value}
                        </div>
                      )}
                      {submission.follow_up_date && (
                        <div>
                          <span className="font-medium">Follow-up:</span>{" "}
                          {new Date(submission.follow_up_date).toLocaleString()}
                        </div>
                      )}
                    </div>

                    {submission.message && (
                      <div className="mt-3 p-3 bg-muted rounded-lg">
                        <p className="text-sm text-muted-foreground font-medium mb-1">Message:</p>
                        <p className="text-sm">{submission.message}</p>
                      </div>
                    )}
                  </div>

                  <ContactLeadActionsMenu
                    contactId={submission.id}
                    contactData={submission}
                    onStatusUpdate={updateStatus}
                    onPriorityUpdate={updatePriority}
                    onRefresh={loadSubmissions}
                  />
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminLeads;
